import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
import type { GridSortModel } from '@mui/x-data-grid';
import { TextField, toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DataGrid } from '@/components/DataGrid';
import { useCompensationReportFilters } from '../CompensationReportFilters/CompensationReportFilters.helpers';
import { ExportToCSVButton } from '../components/ExportToCSVButton';
import {
  buildReportingSummaryQuery,
  createLoadingRows,
  fetchAllPagesReportingSummaryPages,
  fetchUsersAndTeamsMaps,
  mapDataGridColumnToValueOrEmpty,
  useFilterColumnsByUserRole,
  useViewReportAs,
} from '../helpers';
import { useAllTimeStartDate } from '../ReportsConfiguration/useReportsConfiguration';
import { useRoleCanSee } from '../ReportsConfiguration/useReportsRole';
import { buildCsv, downloadCsv } from '../ReportsExport/ReportsExport.helper';
import { TablePagination } from '../TablePagination/TablePagination';
import type { ReportingSummaryQuery } from '../types';
import {
  PAYOUT_BREAKDOWN_DEFAULT_PAGE_SIZE,
  useGetPayoutBreakdown,
} from './hooks/useGetPayoutBreakdown';
import {
  columns,
  mapSummaryToPayoutBreakdownRow,
  PAGE_SIZE_OPTIONS,
  type PayoutBreakdownTableRow,
  payoutBreakdownDataGroups,
  VISIBILITY_MODEL,
} from './PayoutBreakdown.helpers';

const DEFAULT_SORT_MODEL: GridSortModel = [
  { field: 'weightedAdjustedGrossCommission', sort: 'desc' },
];

export function PayoutBreakdownTable() {
  const isMobile = useIsMobile('md');
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<Partial<Record<keyof PayoutBreakdownTableRow, boolean>>>(
      VISIBILITY_MODEL,
    );
  const [groupedBy, setGroupedBy] = useState<'agent' | 'team'>('agent');
  const [isExporting, setIsExporting] = useState(false);

  const {
    rows,
    loading,
    totalCount,
    rowCount,
    paginationModel,
    setPaginationModel,
  } = useGetPayoutBreakdown({ sortModel, groupedBy });

  // Keep the last known totalCount stable so the DataGrid doesn't reset to
  // page 0 when data is loading (totalCount is briefly undefined).
  const rowCountRef = useRef(0);
  if (totalCount !== undefined) rowCountRef.current = totalCount;

  const { filters } = useCompensationReportFilters();
  const viewReportAs = useViewReportAs();
  const allTimeStartDate = useAllTimeStartDate();
  const { roleCanSee } = useRoleCanSee();
  const canSeeTeam = roleCanSee('team');
  const query =
    viewReportAs &&
    buildReportingSummaryQuery(filters, viewReportAs, allTimeStartDate);
  const selectedColumns =
    groupedBy === 'agent' ? columns.groupedByAgent : columns.groupedByTeam;
  const filteredColumns = useFilterColumnsByUserRole({
    columns: selectedColumns,
    dataGroups: payoutBreakdownDataGroups,
  });
  const mappedColumns = useMemo(() => {
    return filteredColumns.map((column) =>
      mapDataGridColumnToValueOrEmpty(column),
    );
  }, [filteredColumns]);
  const isGroupedByAgent = groupedBy === 'agent';

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
      // Reset to page 0 so the new sort starts from the beginning. React 18
      // batches these two setState calls into a single render, preventing a
      // double fetch.
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    },
    [setPaginationModel],
  );

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      const results = await fetchAllPagesReportingSummaryPages({
        offset: 0,
        query: {
          ...query,
          sortBy: sortModel[0].field as ReportingSummaryQuery['sortBy'],
          sortDirection: sortModel[0].sort === 'asc' ? 'ASC' : 'DESC',
          groupBy: [isGroupedByAgent ? 'AGENT' : 'TEAM'],
          includePartiesWithoutTransactions: true,
        },
      });

      if (!results) {
        throw new Error('No results found');
      }

      const { usersById, teamsById } = await fetchUsersAndTeamsMaps({
        userIds: isGroupedByAgent
          ? results.map((r) => r.teamMemberIdentityId)
          : [],
        teamIds: !isGroupedByAgent ? results.map((r) => r.teamIdentityId) : [],
      });
      const exportRows: PayoutBreakdownTableRow[] = results.map((r) =>
        mapSummaryToPayoutBreakdownRow(r, { usersById, teamsById }),
      );
      const csv = buildCsv(exportRows, filteredColumns);
      const filename = `Payout-Breakdown-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      downloadCsv(csv, filename);
      toast('success', 'Payout Breakdown Report exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    } finally {
      setIsExporting(false);
    }
  }, [query, groupedBy, sortModel, filteredColumns, isGroupedByAgent]);

  const handleGroupedByChange = (value: 'agent' | 'team') => {
    setGroupedBy(value);
    setPaginationModel({
      page: 0,
      pageSize: PAYOUT_BREAKDOWN_DEFAULT_PAGE_SIZE,
    });
    setSortModel(DEFAULT_SORT_MODEL);
  };

  const groupBySelector = (
    <TextField
      select
      value={groupedBy}
      onChange={(e) =>
        handleGroupedByChange(e.target.value as 'agent' | 'team')
      }
      size="small"
      sx={{ '& .MuiInputBase-root': { height: { xs: 31, md: 'auto' } } }}
      slotProps={{
        select: {
          renderValue: (value) => (
            <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Group by
              </Typography>
              <Divider orientation="vertical" flexItem />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {value === 'agent' ? 'Agent' : 'Team'}
              </Typography>
            </Stack>
          ),
        },
      }}
    >
      <MenuItem value="agent">Agent</MenuItem>
      <MenuItem value="team">Team</MenuItem>
    </TextField>
  );

  return (
    <Stack sx={{ gap: 2 }}>
      <Stack sx={{ flexDirection: 'column', gap: 2 }}>
        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
              Payout Breakdown
            </Typography>
            {canSeeTeam && groupBySelector}
          </Stack>
          <ExportToCSVButton
            onClick={handleExportCsv}
            loading={isExporting}
            disabled={totalCount === 0}
          />
        </Stack>
        <Box sx={{ position: 'relative' }}>
          <DataGrid
            variant="default"
            columns={mappedColumns}
            slots={{
              pagination: () => (
                <TablePagination
                  variant="compensation"
                  hidePaginationControls={
                    !totalCount ||
                    !rowCount ||
                    totalCount <= PAYOUT_BREAKDOWN_DEFAULT_PAGE_SIZE
                  }
                />
              ),
              noRowsOverlay: () => (
                <Stack
                  direction="column"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <Typography variant="body3" sx={{ color: 'text.secondary' }}>
                    No earnings match the current filters
                  </Typography>
                </Stack>
              ),
            }}
            disableColumnFilter
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            rows={
              loading
                ? createLoadingRows(paginationModel.pageSize, (i) => ({
                    id: String(i),
                    agent: '',
                    agentAvatar: null,
                    team: '',
                  }))
                : rows
            }
            rowCount={rowCountRef.current}
            loading={loading}
            paginationMode="server"
            sortingMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            slotProps={{
              columnsManagement: {
                disableShowHideToggle: false,
                disableResetButton: true,
              },
            }}
            sx={{
              ...(totalCount === 0 && {
                '--DataGrid-overlayHeight': '60px',
              }),
              '& .MuiTablePagination-root': {
                justifyContent: 'flex-start',
                display: 'flex',
              },
              '& .MuiToolbar-root': {
                paddingLeft: 0,
              },
            }}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
